// src/hooks/useStripeActions.js
import { useState, useCallback } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";

/**
 * useStripeActions
 * Provides helpers to start a Stripe Checkout subscription and open the Customer Portal.
 *
 * Options:
 *  - publishableKey: Stripe publishable key (required)
 *  - priceId: default price ID for subscriptions (optional; can also be passed per call)
 *
 * Returns:
 *  - startSubscription({ priceId? }): redirects to Checkout
 *  - openCustomerPortal(): redirects to the portal
 *  - loading: boolean while any action is in-flight
 *  - error: last error (if any)
 */
export default function useStripeActions({
  publishableKey,
  priceId: defaultPriceId,
} = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startSubscription = useCallback(
    async ({ priceId } = {}) => {
      if (!publishableKey) {
        setError(new Error("Stripe publishable key missing."));
        return;
      }
      const pid = priceId || defaultPriceId;
      if (!pid) {
        setError(new Error("No price ID provided."));
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.post(
          "https://ghrianghraffy.com/auth/stripe/create-checkout-session",
          { priceId: pid },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );

        const stripe = await loadStripe(publishableKey);
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } catch (e) {
        console.error("startSubscription error:", e);
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [publishableKey, defaultPriceId]
  );

  const openCustomerPortal = useCallback(async () => {
    if (!publishableKey) {
      setError(new Error("Stripe publishable key missing."));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        "https://ghrianghraffy.com/auth/stripe/create-portal-session",
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Portal URL missing from response.");
      }
    } catch (e) {
      console.error("openCustomerPortal error:", e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [publishableKey]);

  return { startSubscription, openCustomerPortal, loading, error };
}
